import { useDashboard } from '@/hooks/use-dashboard';
import { TrendingUp, TrendingDown, Loader2, Clock, Phone, PhoneMissed, CheckCircle } from 'lucide-react';

const StatCard = ({ title, value, change, icon: Icon, loading }) => {
  const isPositive = change >= 0;
  
  return (
    <div className="bg-orange-50 rounded-lg p-3 flex flex-col justify-between min-h-[90px]">
      {loading ? (
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-5 w-5 text-orange-500 animate-spin" />
        </div>
      ) : (
        <>
          <div className="flex justify-between items-start">
            <span className="text-xl font-semibold text-gray-900">{value}</span>
            {change !== null && change !== undefined && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full flex items-center gap-0.5 ${
                isPositive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {Math.abs(change)}%
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-auto">
            {Icon && <Icon className="h-4 w-4 text-gray-500" />}
            <span className="text-xs text-gray-600">{title}</span>
          </div>
        </>
      )}
    </div>
  );
};

const PeriodSelector = ({ period, onChange }) => {
  const periods = [
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'all', label: 'All Time' }
  ];

  return (
    <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
      {periods.map(p => (
        <button
          key={p.value}
          onClick={() => onChange(p.value)}
          className={`px-2 py-1 text-xs rounded-md transition-colors ${
            period === p.value 
              ? 'bg-white text-orange-600 shadow-sm font-medium' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
};

const TodaysAnalysisDashboard = () => {
  const { stats, loading, period, changePeriod } = useDashboard('today');

  const formatCurrency = (amount) => {
    if (amount >= 1000) {
      return `₹${(amount / 1000).toFixed(1)}k`;
    }
    return `₹${amount}`;
  };

  const formatMinutes = (minutes) => {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}h ${mins}m`;
    }
    return `${minutes}m`;
  };

  return (
    <div className="p-3">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-base font-semibold text-gray-900">Analytics</h2>
        <PeriodSelector period={period} onChange={changePeriod} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
        <StatCard
          title="Earnings"
          value={formatCurrency(stats?.earnings?.amount || 0)}
          change={stats?.earnings?.change}
          loading={loading}
        />
        <StatCard
          title="Total Calls"
          value={stats?.consultations?.total || 0}
          change={stats?.consultations?.change}
          icon={Phone}
          loading={loading}
        />
        <StatCard
          title="Completed"
          value={stats?.consultations?.completed || 0}
          change={null}
          icon={CheckCircle}
          loading={loading}
        />
        <StatCard
          title="Missed"
          value={stats?.consultations?.missed || 0}
          change={null}
          icon={PhoneMissed}
          loading={loading}
        />
        <StatCard
          title="Talk Time"
          value={formatMinutes(stats?.minutes?.total || 0)}
          change={stats?.minutes?.change}
          icon={Clock}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default TodaysAnalysisDashboard;
